import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Home as HomeIcon, Sparkles } from 'lucide-react';
import { SeasonProvider } from './SeasonContext';
import Players from './pages/Players';
import WellnessInput from './pages/WellnessInput';
import PhysicalTestInput from './pages/PhysicalTestInput';
import WellnessStaff from './pages/WellnessStaff';
import GPSUpload from './pages/GPSUpload';
import GPSData from './pages/GPSData';
import MetricsTable from './pages/gps/MetricsTable';
import DrillPerformance from './pages/gps/DrillPerformance';
import MatchPerformance from './pages/gps/MatchPerformance';
import TeamComparison from './pages/gps/TeamComparison';
import PeriodComparison from './pages/gps/PeriodComparison';
import MatchChronic from './pages/gps/MatchChronic';
import TeamWeek from './pages/gps/TeamWeek';
import WeekSummary from './pages/gps/WeekSummary';
import Trends from './pages/gps/Trends';
import Development from './pages/gps/Development';
import InMatchAnalysis from './pages/gps/InMatchAnalysis';
import ComparisonBuilder from './pages/gps/ComparisonBuilder';
import MatchChronicPrint from './pages/gps/MatchChronicPrint';
import IndividualProgressPrint from './pages/IndividualProgressPrint';
import PhysicalTestingPrint from './pages/gps/PhysicalTestingPrint';
import PhysicalTesting from './pages/PhysicalTesting';
import EWMA from './pages/gps/EWMA';
import PlayerChronic from './pages/gps/PlayerChronic';
import MaxSpeed from './pages/gps/MaxSpeed';
import MSS from './pages/gps/MSS';
import IndividualTargets from './pages/gps/IndividualTargets';
import FutureTargets from './pages/gps/FutureTargets';
import GoalkeeperReport from './pages/gps/GoalkeeperReport';
import SessionEditor from './pages/gps/SessionEditor';
import WeekSchedule from './pages/WeekSchedule';
import WeekSchedulePrint from './pages/WeekSchedulePrint';
import Planner from './pages/planner/Planner';
import Overview from './pages/Overview';
import Login from './pages/Login';
import SettingsPage from './pages/Settings';
import Medical from './pages/Medical';
import MedicalBoard from './pages/MedicalBoard';
import Nutrition from './pages/Nutrition';
import ComposeTestMatchChronic from './pages/ComposeTestMatchChronic';
import Availability from './pages/Availability';
import Forms from './pages/forms/Forms';
import TrainerRatings from './pages/TrainerRatings';
import Team360Table from './pages/Team360Table';
import IndividualProgress from './pages/IndividualProgress';
import TeamKpiOverview from './pages/TeamKpiOverview';
import TechnicalOverview from './pages/TechnicalOverview';
import TechnicalComparison from './pages/TechnicalComparison';
import LineupBuilder from './pages/LineupBuilder';
import Ssg from './pages/Ssg';
import SsgTv from './pages/ssg/SsgTv';
import Home from './pages/Home';
import EinsteinChat from './pages/EinsteinChat';
import IndividualReport from './pages/IndividualReport';
import PlayerApp from './pages/player/PlayerApp';
import MatchDay from './pages/matchday/MatchDay';
import UsageTracking from './pages/UsageTracking';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import { ALL_TABS, canOpenSessionEditor } from './tabs';
import { loginUser, getPlayers, getPlayerAliases, getTestInputAccess, trackUsage } from './api';

const GPS_SUB_TABS = [
  { section: 'Team', tabs: [
    { id: 'team-training',       label: 'Team Training' },
    { id: 'ewma',                label: 'EWMA' },
    { id: 'team-week',           label: 'Team Week' },
    { id: 'week-summary',        label: 'Week Summary' },
  ]},
  { section: 'Player', tabs: [
    { id: 'player-chronic',      label: 'Player Chronic' },
    { id: 'max-speed',           label: 'Max Speed' },
    { id: 'mss',                 label: 'MSS Estimate' },
    { id: 'individual-targets',  label: 'Individual Targets' },
    { id: 'future-targets',      label: 'Future Targets' },
    // Development (2026-10-12) — Trends moved under it: reached only via its "Trends" button, so
    // 'trends' is no longer a sub-tab here (or in Settings.js's GPS_SUB_GROUPS).
    { id: 'development',         label: 'Development' },
  ]},
  { section: 'Match', tabs: [
    { id: 'match-performance',   label: 'Match Performance' },
    { id: 'in-match-analysis',   label: 'In-Match Analysis' },
    { id: 'match-chronic',       label: 'Match Chronic' },
  ]},
  { section: 'Goalkeeper', tabs: [
    { id: 'goalkeeper',         label: 'Goalkeeper' },
  ]},
  { section: 'Rehab', tabs: [
    { id: 'rehab-training',     label: 'Training overview' },
  ]},
  // The Session Editor was the admin-only "Sessions" sub-tab here until 2026-10-31; it is now its own page
  // ('session-editor'), opened from a button on the Upload zone (see tabs.js's canOpenSessionEditor).
];

function GpsPlaceholder({ label }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', opacity: 0.4, textAlign: 'center' }}>
      <h2 style={{ fontSize: 22, fontWeight: 600 }}>{label}</h2>
      <p style={{ fontSize: 14, marginTop: 8 }}>Coming soon.</p>
    </div>
  );
}

// ── App shell ──────────────────────────────────────────────────────────────
function PageContent({ page, user, gpsTab, onNavigate }) {
  // A chef account sees ONLY Nutrition — nothing else on the platform, no exceptions, even if
  // the URL's ?page= is changed by hand. Checked first, before any other `page` branch below,
  // so it can never fall through to one of them. Server-side, the same restriction is enforced
  // independently by server.staging.js's role-gating middleware (a hidden/blocked UI route
  // alone wouldn't stop a direct API call) — this UI guard and that server guard are separate,
  // deliberately redundant layers, not each other's only protection.
  if (user.role === 'chef') return page === 'nutrition' ? <Nutrition user={user} /> : null;
  if (page === 'home')           return <Home user={user} onNavigate={onNavigate} />;
  if (page === 'einstein')       return <EinsteinChat user={user} onNavigate={onNavigate} />;
  if (page === 'individualreport') return <IndividualReport user={user} onNavigate={onNavigate} />;
  if (page === 'overview')      return <Overview user={user} onNavigate={onNavigate} />;
  if (page === 'players')       return <Players user={user} />;
  if (page === 'wellness')      return <WellnessInput />;
  if (page === 'testinput')     return <PhysicalTestInput currentUser={user} />;
  if (page === 'testing')       return <PhysicalTesting onNavigate={onNavigate} />;
  if (page === 'wellnessstaff') return <WellnessStaff />;
  if (page === 'gpsupload')     return <GPSUpload user={user} onNavigate={onNavigate} />;
  // Session Editor (2026-10-31): opened from the Upload zone's button; Upload zone access + the Session Editor
  // permission (admins always). Its back link returns to the Upload zone through the editor's unsaved-changes check.
  if (page === 'session-editor') return canOpenSessionEditor(user) ? <SessionEditor user={user} onBack={() => onNavigate('gpsupload')} /> : null;
  if (page === 'settings')      return <SettingsPage user={user} onNavigate={onNavigate} />;
  if (page === 'usage-tracking') return user.isAdmin ? <UsageTracking user={user} onNavigate={onNavigate} /> : null;
  if (page === 'medical')       return <Medical user={user} />;
  // Admin-role only (per spec) — same hard guard as usage-tracking, on top of the tabs.js
  // allowedTabs gate. The player-facing diary lives in the player app, not here.
  if (page === 'nutrition')     return user.isAdmin ? <Nutrition user={user} /> : null;
  if (page === 'forms')         return <Forms user={user} onNavigate={onNavigate} />;
  if (page === 'trainerratings') return <TrainerRatings user={user} onNavigate={onNavigate} />;
  if (page === 'team360table')   return <Team360Table user={user} onNavigate={onNavigate} />;
  if (page === 'individual-progress') return <IndividualProgress user={user} onNavigate={onNavigate} />;
  // Stage 6a: Home's Tactical/Identity bubbles link here — one shared component (see
  // TeamKpiOverview.js) parameterized by pillar, not two page implementations.
  if (page === 'tactical-overview') return <TeamKpiOverview pillar="tactical" user={user} onNavigate={onNavigate} />;
  if (page === 'identity-overview') return <TeamKpiOverview pillar="identity" user={user} onNavigate={onNavigate} />;
  // Stage 6b: Technical's squad view — grouped by position (one table per position), unlike
  // TeamKpiOverview's single-table shape, since Technical's KPI set differs per position. Not
  // parameterized into TeamKpiOverview — different enough data shape to warrant its own page.
  if (page === 'technical-overview') return <TechnicalOverview user={user} onNavigate={onNavigate} />;
  if (page === 'technical-comparison') return <TechnicalComparison user={user} onNavigate={onNavigate} />;
  // Reached via the "Advanced metrics table" button on Team Training (see GPSData.js), not the
  // GPS sub-tab bar — see tabs.js's HOME_ONLY_TABS comment on this id for why it's gated there
  // instead of GPS_SUB_TABS/GPS_SUB_GROUPS.
  if (page === 'metrics-table') return <MetricsTable user={user} onNavigate={onNavigate} />;
  // Reached via a button on the Advanced Metrics Table (see MetricsTable.js), same pattern as
  // metrics-table itself above.
  if (page === 'drill-performance') return <DrillPerformance user={user} onNavigate={onNavigate} />;
  // Stage 7b: interactive squad builder, no persistence yet (Stage 7e/7f).
  if (page === 'lineup-builder') return <LineupBuilder user={user} onNavigate={onNavigate} />;
  if (page === 'ssg') return <Ssg user={user} onNavigate={onNavigate} />;
  if (page === 'availability')  return <Availability user={user} onNavigateToMedical={() => onNavigate('medical')} />;
  // Home's Availability bubble jumps straight to Season Analysis rather than the daily
  // roster (2026-07-22) — a distinct page id so the roster tab stays the normal landing spot
  // everywhere else Availability is reached from.
  if (page === 'availability-analysis') return <Availability user={user} onNavigateToMedical={() => onNavigate('medical')} initialTab="analysis" />;
  if (page === 'weekschedule') return <WeekSchedule user={user} />;
  if (page === 'planner') return <Planner user={user} />;
  if (page === 'matchday') return <Navigate to="/matchday" replace />;
  if (page === 'gps') {
    if (gpsTab === 'team-training') return <GPSData user={user} onNavigate={onNavigate} />;
    if (gpsTab === 'ewma') return <EWMA />;
    if (gpsTab === 'player-chronic') return <PlayerChronic />;
    if (gpsTab === 'max-speed') return <MaxSpeed user={user} />;
    if (gpsTab === 'mss') return <MSS user={user} />;
    if (gpsTab === 'individual-targets') return <IndividualTargets />;
    if (gpsTab === 'future-targets') return <FutureTargets />;
    if (gpsTab === 'match-performance') return <MatchPerformance user={user} onNavigate={onNavigate} />;
    if (gpsTab === 'team-comparison') return <TeamComparison user={user} onNavigate={onNavigate} />;
    // Reached only via Team Comparison's "Advanced comparison" button, not the sub-tab bar.
    if (gpsTab === 'period-comparison') return <PeriodComparison onNavigate={onNavigate} />;
    if (gpsTab === 'match-chronic')     return <MatchChronic />;
    if (gpsTab === 'team-week')         return <TeamWeek />;
    if (gpsTab === 'week-summary')      return <WeekSummary user={user} />;
    // Reached only via Development's "Trends" button, not the sub-tab bar.
    if (gpsTab === 'trends')            return <Trends user={user} onNavigate={onNavigate} />;
    if (gpsTab === 'development')       return <Development user={user} onNavigate={onNavigate} />;
    if (gpsTab === 'in-match-analysis') return <InMatchAnalysis onNavigate={onNavigate} />;
    if (gpsTab === 'comparison-builder') return <ComparisonBuilder onNavigate={onNavigate} user={user} />;
    if (gpsTab === 'goalkeeper') return <GoalkeeperReport />;
    if (gpsTab === 'rehab-training') return <GPSData user={user} mode="rehab" />;
    const label = GPS_SUB_TABS.flatMap(g => g.tabs).find(t => t.id === gpsTab)?.label || '';
    return <GpsPlaceholder label={label} />;
  }
  const item = ALL_TABS.find(n => n.id === page);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.4, textAlign: 'center' }}>
      {item && <item.icon size={64} strokeWidth={1} />}
      <h2 style={{ fontSize: 22, fontWeight: 600, marginTop: 16 }}>{item?.label}</h2>
      <p style={{ fontSize: 14, marginTop: 8 }}>This section is coming soon.</p>
    </div>
  );
}

function AppShell({ user, onLogout }) {
  const navigate = useNavigate();
  const [hasTestInputAccess, setHasTestInputAccess] = useState(false);

  useEffect(() => {
    if (user.isAdmin) return;
    getTestInputAccess()
      .then(list => setHasTestInputAccess((list || []).some(a => a.id === user.id && a.granted_at)))
      .catch(() => {});
  }, [user.id, user.isAdmin]);

  // Chef: the tab bar shows Nutrition and nothing else, full stop — not derived from
  // allowedTabs (which admin can otherwise edit per account in Settings), so it can't
  // accidentally be widened the way a staff account's tab list can.
  const visibleTabs = user.role === 'chef'
    ? ALL_TABS.filter(t => t.id === 'nutrition')
    : ALL_TABS.filter(t => {
        // Admin-only tab (PageContent enforces the same restriction) — previously any
        // account with 'nutrition' checked in Settings would see the button even though
        // opening it did nothing for them; now it simply never shows for a non-admin.
        if (t.id === 'nutrition') return user.isAdmin;
        if (t.id === 'testinput') return user.isAdmin || hasTestInputAccess;
        // Edit rights on the Planner imply seeing it (services/planner/routes.js treats them the same way).
        if (t.id === 'planner') return user.isAdmin || (user.allowedTabs || []).some(id => id === 'planner' || id === 'planner-edit');
        return t.id === 'settings' || user.isAdmin || (user.allowedTabs || []).includes(t.id);
      });

  const allGpsSubIds = GPS_SUB_TABS.flatMap(g => g.tabs.filter(t => !t.adminOnly || user.isAdmin).map(t => t.id));
  const allowedGpsIds = user.isAdmin || user.allowedGpsTabs == null
    ? allGpsSubIds
    : user.allowedGpsTabs;

  const visibleGpsGroups = GPS_SUB_TABS
    .map(g => ({ ...g, tabs: g.tabs.filter(t => allowedGpsIds.includes(t.id) && (!t.adminOnly || user.isAdmin)) }))
    .filter(g => g.tabs.length > 0);
  const visibleGpsTabIds = visibleGpsGroups.flatMap(g => g.tabs.map(t => t.id));
  const defaultGpsTab = visibleGpsTabIds[0] || 'team-training';

  // Home used to be the unconditional app root for everyone (confirmed 2026-07-23). As of
  // 2026-07-29 it's gated like every other tab (see tabs.js's HOME_ONLY_TABS comment) — the
  // default landing page for accounts without 'home' access falls back to the first tab bar
  // entry (visibleTabs, same top-nav order as ALL_TABS) they actually have access to, not a
  // hardcoded 'overview' — accounts without Team Overview access (e.g. physio-only accounts)
  // would otherwise see it anyway on login, since it briefly rendered before their own
  // access check elsewhere could redirect away. Lazy initializer since `user` (loaded from
  // localStorage before AppShell mounts) needs to already be known to decide this on first
  // render.
  const canAccessHome = user.isAdmin || (user.allowedTabs || []).includes('home');
  // Einstein (2026-09-10) — split out from canAccessHome above, which it used to piggyback on
  // ("same underlying capability, not a separate permission"). Now its own tabs.js/allowedTabs
  // entry, defaulted off for new non-admin accounts, granted per-account from Settings as the
  // feature gets tested more (explicit request).
  const canAccessEinstein = user.isAdmin || (user.allowedTabs || []).includes('einstein');
  // 2026-08-24 (Einstein Task 3 foundation) — page/gpsTab now seed from the URL so a
  // constructed deep link (?page=gps&gpsTab=ewma&player_id=42) actually lands on the right
  // page, not just the right filter within whatever page happened to already be open. Applies
  // the same 'home' redirect handlePageChange already enforces for button clicks — a
  // ?page=home link from an unauthorized account resolves the same way a click would, rather
  // than skipping that check just because it arrived via the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  // The Session Editor left GPS & Load on 2026-10-31: an old link (?page=gps&gpsTab=session-editor) opens its own page
  // when the account may use it, and the account's normal landing page otherwise; so does ?page=session-editor
  // without the permission. The URL is rewritten once on mount (below), so nothing keeps pointing at the old place.
  const landingPage = canAccessHome ? 'home' : (visibleTabs[0]?.id || 'settings');
  const legacyEditorLink = searchParams.get('page') === 'gps' && searchParams.get('gpsTab') === 'session-editor';
  const [activePage, setActivePage] = useState(() => {
    const fromUrl = searchParams.get('page');
    if (legacyEditorLink || fromUrl === 'session-editor') return canOpenSessionEditor(user) ? 'session-editor' : landingPage;
    if (fromUrl) return (fromUrl === 'home' && !canAccessHome) ? (visibleTabs[0]?.id || 'settings') : fromUrl;
    return landingPage;
  });
  const [activeGpsTab, setActiveGpsTab] = useState(() => {
    const fromUrl = searchParams.get('gpsTab');
    return fromUrl && fromUrl !== 'session-editor' ? fromUrl : defaultGpsTab;
  });
  useEffect(() => {
    if (searchParams.get('gpsTab') !== 'session-editor' && (searchParams.get('page') !== 'session-editor' || activePage === 'session-editor')) return;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('page', activePage);
      next.delete('gpsTab');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2026-08-23: page-view logging for the admin Usage Analytics page. Fires on the same state
  // that drives PageContent (activePage, plus activeGpsTab while on the GPS page — GPS sub-tabs
  // are switched via their own state, not handlePageChange, so activePage alone stays 'gps' the
  // whole time a user browses Team Training / Player Chronic / Match Performance / etc. Logging
  // just 'gps' would hide which of those they actually looked at.
  useEffect(() => {
    const trackedPage = activePage === 'gps'
      ? (visibleGpsTabIds.length === 1 ? visibleGpsTabIds[0] : activeGpsTab)
      : activePage;
    trackUsage(user.id, user.username, user.name, trackedPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, activeGpsTab]);

  // 2026-07-30: hiding the nav buttons that request 'home' isn't enough on its own — this is
  // the single choke point every caller of onNavigate/handlePageChange goes through (the header's
  // "Back to Home" button below, PageChrome's own gated nav pill, any future button that calls
  // onNavigate('home')), so it's where access actually gets enforced, not just where it's hidden.
  // An unauthorized 'home' request quietly resolves to the first tab the account can access
  // instead of rendering Home.
  // `extraParams` (2026-09-03, Einstein route_to_page/compose links): a URLSearchParams of
  // filter values (player_id, date, ...) to merge in alongside the page/gpsTab switch, in the
  // SAME setSearchParams call — not a second separate one. Two independent setSearchParams calls
  // in one click handler risk one clobbering the other's `prev` snapshot (react-router's search
  // params aren't a plain useState cell with the same same-batch-chaining guarantee); a single
  // merged update has no such race. This is also *why* Einstein's "go to page" links didn't work
  // at all before this fix: the old goToRoute called react-router's navigate() directly, which
  // updates the URL but never calls setActivePage/setActiveGpsTab — those are plain React state
  // in this component, seeded from the URL only once at mount (see the lazy useState above), so
  // the page silently never actually changed.
  const handlePageChange = (id, gpsTab, extraParams) => {
    // Match Day is its own full-screen route (touchline iPad), not a page inside this shell.
    if (id === 'matchday') { navigate('/matchday'); return; }
    const nextPage = id === 'home' && !canAccessHome ? (visibleTabs[0]?.id || 'settings') : id;
    setActivePage(nextPage);
    if (gpsTab) setActiveGpsTab(gpsTab);
    if (window.location.pathname !== '/app') navigate('/app', { replace: true });
    // Merge, don't replace: a page change shouldn't drop filter params a leaf page already
    // wrote (e.g. clicking into GPS from elsewhere with player_id already set). gpsTab is
    // cleared when leaving GPS so a stale sub-tab doesn't linger in the URL for other pages.
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('page', nextPage);
      if (gpsTab) next.set('gpsTab', gpsTab);
      else if (nextPage !== 'gps') next.delete('gpsTab');
      if (extraParams) { for (const [k, v] of extraParams.entries()) next.set(k, v); }
      return next;
    }, { replace: true });
  };

  const handleGpsTabChange = (tabId) => {
    setActiveGpsTab(tabId);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('gpsTab', tabId);
      return next;
    }, { replace: true });
  };

  // Home, Individual Report, Team Table, and Trainer Ratings all share Home's own chrome
  // (PageChrome — stadium background + HeraLabX top bar) instead of AppShell's header/tab
  // bar. Team Table and Trainer Ratings moved here from the main tab bar on 2026-07-22 — see
  // tabs.js — so they need the same hideHeader treatment Home/Individual Report already had.
  const hideHeader = ['home', 'einstein', 'individualreport', 'team360table', 'individual-progress', 'trainerratings', 'tactical-overview', 'identity-overview', 'technical-overview', 'technical-comparison', 'lineup-builder', 'ssg'].includes(activePage);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--bg-app)', color: 'var(--text-primary)', fontFamily: 'Inter, sans-serif' }}>
      {!hideHeader && (
      <header style={{ borderBottom: '1px solid var(--border-strong)', background: 'var(--bg-app)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img
              src={`${process.env.PUBLIC_URL}/heracles-crest.png`}
              alt="Heracles Almelo"
              style={{ height: 40, width: 'auto' }}
            />
            <div style={{ fontWeight: 900, fontSize: 22, fontStyle: 'italic', fontFamily: "'Arial Black', Impact, sans-serif", letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
              HeraLab<span style={{ color: '#2563eb' }}>X</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{user?.name || user?.username}</span>
              <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>{user?.isAdmin ? 'Administrator' : user?.role === 'player' ? 'Player' : user?.role === 'chef' ? 'Chef' : 'Staff'}</span>
            </div>
            {canAccessHome && (
              <button
                type="button"
                onClick={() => handlePageChange('home')}
                title="Back to Home"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 14px', borderRadius: 12, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
              >
                <HomeIcon size={14} /> Home
              </button>
            )}
            {/* Dedicated Einstein tab (pages/EinsteinChat.js, 2026-09-03) — a full-page Claude-
                like chat, distinct from the compact console embedded in Home's lower half
                (EinsteinConsole.js), which stays for quick questions without leaving Home. Gated
                on its own 'einstein' permission (2026-09-10) — see canAccessEinstein above.
                PageChrome.js mirrors this pill for the pages that use that chrome instead of this
                header — see its NAV_ITEMS comment for why it deliberately coexists with Squad
                Builder's unrelated same-named auto-fill button (2026-09-03 decision). */}
            {canAccessEinstein && (
              <button
                type="button"
                onClick={() => handlePageChange('einstein')}
                title="Ask Einstein"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 14px', borderRadius: 12, border: '1px solid #22d3ee', background: 'rgba(34,211,238,0.14)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
              >
                <span style={{ width: 6, height: 6, borderRadius: 999, background: '#22d3ee' }} />
                <Sparkles size={14} color="#22d3ee" /> Einstein
              </button>
            )}
            <button
              type="button"
              onClick={() => { onLogout(); navigate('/login', { replace: true }); }}
              style={{ padding: '10px 14px', borderRadius: 12, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
            >
              Sign out
            </button>
          </div>
        </div>
        <nav style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 24px 14px' }}>
          {visibleTabs.map(({ icon: Icon, label, id }) => {
            // the Session Editor is opened from the Upload zone, so the Upload zone stays marked while it is open
            const on = activePage === id || (id === 'gpsupload' && activePage === 'session-editor');
            return (
            <button key={id} onClick={() => handlePageChange(id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 11px',
                borderRadius: 10,
                border: on ? '1px solid #3b82f6' : '1px solid transparent',
                background: on ? '#3b82f6' : 'var(--bg-card)',
                color: on ? '#fff' : 'var(--text-secondary)',
                fontWeight: on ? 600 : 500,
                fontSize: 12,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease',
              }}
            >
              <Icon size={14} strokeWidth={1.8} />
              <span>{label}</span>
            </button>
            );
          })}
        </nav>
        {activePage === 'gps' && visibleGpsTabIds.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'flex-end', padding: '10px 24px 10px', background: 'var(--bg-app)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border-strong)', gap: 0, overflowX: 'auto' }}>
            {visibleGpsGroups.map((group, gi) => (
              <React.Fragment key={group.section}>
                {gi > 0 && <div style={{ width: 1, alignSelf: 'stretch', background: '#1e3a5f', margin: '0 14px', flexShrink: 0 }} />}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-dimmed)', letterSpacing: '0.1em', textTransform: 'uppercase', paddingLeft: 4 }}>{group.section}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    {group.tabs.map(tab => (
                      <button key={tab.id} onClick={() => handleGpsTabChange(tab.id)}
                        style={{
                          padding: '4px 10px',
                          borderRadius: 99,
                          border: activeGpsTab === tab.id ? '1px solid #3b82f6' : '1px solid var(--border)',
                          background: activeGpsTab === tab.id ? '#3b82f6' : 'transparent',
                          color: activeGpsTab === tab.id ? '#fff' : 'var(--text-muted)',
                          fontWeight: activeGpsTab === tab.id ? 600 : 400,
                          fontSize: 12,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >{tab.label}</button>
                    ))}
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </header>
      )}
      <main style={{ flex: 1, overflow: 'auto', padding: 24, background: 'var(--bg-app)' }}>
        <PageContent page={activePage} user={user} gpsTab={visibleGpsTabIds.length === 1 ? visibleGpsTabIds[0] : activeGpsTab} onNavigate={handlePageChange} />
      </main>
    </div>
  );
}

function ProtectedRoute({ user, children }) {
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// ── Root ───────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('authUser')) || null; }
    catch { return null; }
  });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    // Debounced: a spurious/transient resize (browser chrome, on-screen
    // keyboard) can otherwise flip isMobile true-then-false within
    // milliseconds, which swaps mobile/desktop GPS components in and out
    // and fires two near-simultaneous /api/gps requests that can hang.
    let timer;
    const handleResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth < 768), 200);
    };
    window.addEventListener('resize', handleResize);
    return () => { clearTimeout(timer); window.removeEventListener('resize', handleResize); };
  }, []);

  useEffect(() => {
    if (user) localStorage.setItem('authUser', JSON.stringify(user));
    else localStorage.removeItem('authUser');
  }, [user]);

  const handleLogin = async (username, password) => {
    if (!username || !password) return 'Username and password are required.';
    try {
      const loggedIn = await loginUser(username, password);
      if (loggedIn.role === 'player') {
        try {
          const players = await getPlayers();
          const displayName = (loggedIn.displayName || loggedIn.name || loggedIn.username || '').trim().toLowerCase();
          let playerRecord = players.find(p =>
            (p.name || '').trim().toLowerCase() === displayName
          ) || null;
          if (!playerRecord) {
            const aliases = await getPlayerAliases();
            const alias = aliases.find(a => (a.alias_name || '').trim().toLowerCase() === displayName);
            if (alias) {
              playerRecord = players.find(p => p.id === alias.player_id) || null;
            }
          }
          loggedIn.playerRecord = playerRecord;
        } catch {
          loggedIn.playerRecord = null;
        }
      }
      setUser(loggedIn);
      return null;
    } catch {
      return 'Invalid username or password.';
    }
  };

  const handleLogout = () => setUser(null);

  const defaultPath = !user ? '/login' : user.role === 'player' ? '/player' : '/app';

  return (
    <SeasonProvider>
    <Routes>
      <Route path="/print/match-chronic" element={<MatchChronicPrint />} />
      <Route path="/print/individual-progress" element={<IndividualProgressPrint />} />
      <Route path="/print/physical-testing" element={<PhysicalTestingPrint />} />
      <Route path="/print/week-schedule" element={<WeekSchedulePrint />} />
      {/* No login, token-only (see /api/ssg/tv) — a dressing-room kiosk device just opens this
          URL directly, same "public utility route" tier as /print/* above. */}
      <Route path="/ssg/tv" element={<SsgTv />} />
      {/* Public, no login — Oura's consent screen and any external link to these need to work
          for someone who isn't signed into HeraLabX at all. Same tier as /print/* and /ssg/tv. */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/login" element={<Login onLogin={handleLogin} />} />
      {/* Match Day (2026-10-18) — full-screen touchline page; access checked in MatchDay and server-side. */}
      <Route path="/matchday" element={
        <ProtectedRoute user={user}>
          {user?.role === 'player' ? <Navigate to="/player" replace /> : <MatchDay user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/board" element={
        <ProtectedRoute user={user}>
          <MedicalBoard />
        </ProtectedRoute>
      } />
      {/* Einstein Phase 1, Task 3 proof-of-concept — throwaway test route, not a product page. */}
      <Route path="/einstein-compose-test" element={
        <ProtectedRoute user={user}>
          <ComposeTestMatchChronic />
        </ProtectedRoute>
      } />
      <Route path="/app" element={
        <ProtectedRoute user={user}>
          {user?.role === 'player'
            ? <Navigate to="/player" replace />
            : <AppShell user={user} onLogout={handleLogout} />}
        </ProtectedRoute>
      } />
      <Route path="/player/*" element={
        <ProtectedRoute user={user}>
          {user?.role === 'player'
            ? <PlayerApp user={user} isMobile={isMobile} onLogout={handleLogout} />
            : <Navigate to="/app" replace />}
        </ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to={defaultPath} replace />} />
      <Route path="*" element={<Navigate to={defaultPath} replace />} />
    </Routes>
    </SeasonProvider>
  );
}
